import React, { useState, useRef, useEffect } from 'react';
import { db, storage } from '../../firebase';
import { doc, updateDoc, serverTimestamp, getDoc, collection, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { X, Plus, Upload, ChevronDown, Image, User, Users, Trash2 } from 'lucide-react';
import CollaboratorsModal from './CollaboratorsModal';

const EditShowModal = ({ isOpen, onClose, showId, show }) => {
  const [showName, setShowName] = useState(show?.showName || '');
  const [showDescription, setShowDescription] = useState(show?.showDescription || '');
  const [showImage, setShowImage] = useState(null);
  const [showImagePreview, setShowImagePreview] = useState(show?.showImage || '');
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [showSeasonSelector, setShowSeasonSelector] = useState(false);
  const [showCastSeason, setShowCastSeason] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [descriptionError, setDescriptionError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [showCollaboratorsModal, setShowCollaboratorsModal] = useState(false);
  const [collaborators, setCollaborators] = useState([]);
  const [hasEpisodesInSeason, setHasEpisodesInSeason] = useState(true);
  
  const fileInputRef = useRef(null);
  
  // Initialize showCastSeason state from show data
  useEffect(() => {
    if (show && show.showCastSeason) {
      setShowCastSeason(show.showCastSeason);
    } else {
      // Initialize empty cast object with season 1
      setShowCastSeason({ 1: [] });
    }
  }, [show]);
  
  // Validate inputs when they change
  useEffect(() => {
    // Check name length - change from 40 to 21
    if (showName.length > 21) {
      setNameError(true);
    } else {
      setNameError(false);
    }
    
    // Check description word count
    const wordCount = showDescription.trim().split(/\s+/).length;
    if (showDescription.trim() !== '' && wordCount > 31) {
      setDescriptionError(true);
    } else {
      setDescriptionError(false);
    }
  }, [showName, showDescription]);
  
  // Check if current season has episodes
  useEffect(() => {
    const checkSeasonEpisodes = async () => {
      if (!showId || !selectedSeason) return;
      
      try {
        const episodesRef = collection(db, 'shows', showId, 'episodes');
        const episodesSnapshot = await getDocs(episodesRef);
        
        // Filter episodes for the current season
        const episodesInSeason = episodesSnapshot.docs.filter(
          doc => doc.data().episodeSeason === selectedSeason
        );
        
        setHasEpisodesInSeason(episodesInSeason.length > 0);
      } catch (err) {
        console.error('Error checking season episodes:', err);
        // Default to true to prevent accidental deletion
        setHasEpisodesInSeason(true);
      }
    };
    
    checkSeasonEpisodes();
  }, [showId, selectedSeason]);
  
  useEffect(() => {
    const fetchCollaborators = async () => {
      if (!showId) return;
      
      try {
        const showRef = doc(db, 'shows', showId);
        const showDoc = await getDoc(showRef);
        
        if (showDoc.exists()) {
          const showData = showDoc.data();
          const collaboratorsData = showData.collaborators || [];
          
          // If there are collaborator IDs, fetch their details
          if (collaboratorsData.length > 0) {
            const collaboratorsDetails = [];
            
            for (const collaboratorId of collaboratorsData) {
              const userRef = doc(db, 'users', collaboratorId);
              const userDoc = await getDoc(userRef);
              
              if (userDoc.exists()) {
                const userData = userDoc.data();
                collaboratorsDetails.push({
                  id: userDoc.id,
                  name: userData.name,
                  handle: userData.handle,
                  photoURL: userData.photoURL
                });
              }
            }
            
            setCollaborators(collaboratorsDetails);
          }
        }
      } catch (err) {
        console.error('Error fetching collaborators:', err);
      }
    };
    
    fetchCollaborators();
  }, [showId]);
  
  if (!isOpen) return null;
  
  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setShowImage(file);
      setShowImagePreview(URL.createObjectURL(file));
    }
  };
  
  const handleImageClick = () => {
    fileInputRef.current.click();
  };
  
  const handleAddCastMember = () => {
    // Make sure the season exists in the cast object
    if (!showCastSeason[selectedSeason]) {
      setShowCastSeason({...showCastSeason, [selectedSeason]: []});
    }
    
    // Only add if we have less than 8 cast members
    if (showCastSeason[selectedSeason].length < 8) {
      const newCast = {...showCastSeason};
      newCast[selectedSeason] = [
        ...newCast[selectedSeason], 
        '' // Just add an empty string
      ];
      setShowCastSeason(newCast);
    }
  };
  
  const handleRemoveCastMember = (index) => {
    const newCast = {...showCastSeason};
    newCast[selectedSeason] = newCast[selectedSeason].filter((_, i) => i !== index);
    setShowCastSeason(newCast);
  };
  
  const handleCastChange = (index, value) => {
    const newCast = {...showCastSeason};
    newCast[selectedSeason][index] = value;
    setShowCastSeason(newCast);
  };
  
  const handleSeasonChange = (season) => {
    setSelectedSeason(season);
    setShowSeasonSelector(false);
    
    // Initialize cast for this season if it doesn't exist
    if (!showCastSeason[season]) {
      setShowCastSeason({...showCastSeason, [season]: []});
    }
  };
  
  const handleCreateNewSeason = () => {
    const newSeasonNumber = (show.currentSeason || 1) + 1;
    
    // Initialize cast for the new season
    setShowCastSeason({
      ...showCastSeason,
      [newSeasonNumber]: []
    });
    
    // Select the new season
    setSelectedSeason(newSeasonNumber);
  };
  
  const handleDeleteSeason = async () => {
    if (hasEpisodesInSeason) {
      setError("Cannot delete a season that has episodes. Delete all episodes first.");
      return;
    }
    
    if (selectedSeason === 1) {
      setError("Cannot delete Season 1. A show must have at least one season.");
      return;
    }
    
    try {
      // Remove the season from the cast object
      const newCast = {...showCastSeason};
      delete newCast[selectedSeason];
      setShowCastSeason(newCast);
      
      // Set selected season to the previous one
      setSelectedSeason(selectedSeason - 1);
      
      // Note: We'll update the show's currentSeason in the handleSubmit function
    } catch (err) {
      console.error('Error deleting season:', err);
      setError('Failed to delete season. Please try again.');
    }
  };
  
  const handleSubmit = async () => {
    if (!showName.trim() || nameError || descriptionError) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // Upload show cover image if changed
      let showImageUrl = show.showImage;
      if (showImage) {
        const imageRef = ref(storage, `shows/${showId}/cover_${Date.now()}`);
        await uploadBytes(imageRef, showImage);
        showImageUrl = await getDownloadURL(imageRef);
      }
      
      // Update show document
      const showRef = doc(db, 'shows', showId);
      
      // Clean up cast data - remove empty entries
      const cleanedCast = {};
      Object.keys(showCastSeason).forEach(season => {
        cleanedCast[season] = showCastSeason[season].filter(name => name.trim() !== '');
      });
      
      // Calculate the current season (highest season number)
      const currentSeason = Math.max(...Object.keys(cleanedCast).map(Number));
      
      const updatedShowData = {
        showName,
        showDescription,
        showImage: showImageUrl,
        showCastSeason: cleanedCast,
        currentSeason,
        lastUpdated: serverTimestamp()
      };
      
      await updateDoc(showRef, updatedShowData);
      
      // Create a complete updated show object to return to the parent
      const completeUpdatedShow = {
        ...show,
        ...updatedShowData,
        showImage: showImageUrl,
        // Ensure we include any fields that might be in the original show object
        id: showId,
        createdBy: show.createdBy,
        createdAt: show.createdAt,
        collaborators: show.collaborators || []
      };
      
      // Close the modal and pass updated data back to parent
      onClose(completeUpdatedShow);
      
    } catch (err) {
      console.error('Error updating show:', err);
      setError('Failed to update show. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      zIndex: 1100,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '1rem'
    }}>
      <div style={{
        padding: '1.5rem',
        backgroundColor: '#6600cc',
        borderRadius: '0.75rem',
        maxWidth: '45rem',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem'
        }}>
          <h2 style={{
            margin: 0,
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: 'white'
          }}>
            Edit Show
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>
        
        <div style={{
          display: 'flex',
          gap: '1.5rem',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          marginBottom: '1.5rem'
        }}>
          {/* Left side - Image upload */}
          <div style={{
            width: '12rem',
            flexShrink: 0
          }}>
            {/* Cover image upload */}
            <div style={{
              position: 'relative'
            }}>
              <div 
                onClick={handleImageClick}
                style={{
                  aspectRatio: '2/3',
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  borderRadius: '.8rem',
                  display: 'flex',
                  width: '12rem',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  border: '2px dashed grey',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {showImagePreview ? (
                  <img 
                    src={showImagePreview} 
                    alt="Show cover" 
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <>
                    <Image size={44} color="#666" />
                    <div style={{ 
                      marginTop: '0.5rem', 
                      color: '#999',
                      fontSize: '0.8rem',
                      textAlign: 'center'
                    }}>
                      Show Cover
                    </div>
                  </>
                )}
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageChange}
                style={{ display: 'none' }}
                accept="image/*"
              />
            </div>
          </div>
          
          {/* Right side - Show details form */}
          <div style={{
            flex: 1,
            minWidth: '15rem', marginRight: '2rem'
          }}>
            
            <div style={{display: 'flex'}}>
            {/* Show name field */}
            <div style={{ 
              marginBottom: '1.5rem',
              position: 'relative'
            }}>


              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.9rem',
                  color: 'white'
                }}>
                  Show Name
                </label>
              
              </div>
              
              <input
                type="text"
                value={showName}
                onChange={(e) => setShowName(e.target.value)}
                placeholder="Enter show name"
                style={{
                  width: '20rem',
                  padding: '0.75rem',
                  fontWeight: '600',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: nameError ? '1px solid #E50914' : 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '0.9rem'
                }}
              />
              {nameError && (
                <div style={{
                  color: '#E50914',
                  fontSize: '0.8rem',
                  marginTop: '0.25rem'
                }}>
                  Show name must be 21 characters or less
                </div>
              )}
              <div style={{
                fontSize: '0.8rem',
                color: '#999',
                marginTop: '0.25rem',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span>{showName.length}/21 characters</span>
              </div>
            </div>
              
            <button
                  onClick={() => setShowCollaboratorsModal(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '3rem',
                    height: '3rem',
                    marginLeft: 'auto',
                    marginTop: '1rem',
                    borderRadius: '50%',
                    backgroundColor: collaborators.length > 0 ? 'white' : 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  <Users 
                    size={20} 
                    color={collaborators.length > 0 ? '#6600cc' : 'white'} 
                  />
                </button>
                </div>
            
            {/* Description field */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                color: 'white'
              }}>
                Description
              </label>
              <textarea
                value={showDescription}
                onChange={(e) => setShowDescription(e.target.value)}
                placeholder="Enter show description"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: descriptionError ? '1px solid #E50914' : 'none',
                  borderRadius: '0.5rem',
                  color: 'white',
                  fontSize: '0.9rem',
                  minHeight: '6rem',
                  resize: 'vertical'
                }}
              />
              {descriptionError && (
                <div style={{
                  color: '#E50914',
                  fontSize: '0.8rem',
                  marginTop: '0.25rem'
                }}>
                  Description must be 31 words or less
                </div>
              )}
              <div style={{
                fontSize: '0.8rem',
                color: '#999',
                marginTop: '0.25rem',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span>{showDescription.trim().split(/\s+/).length}/31 words</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Cast section - Full width */}
        <div style={{
          marginTop: '1rem',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderRadius: '0.6rem',
          padding: '1rem',
          width: 'calc(100% - 3rem)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Users size={18} color="white" />
              <h3 style={{
                margin: 0,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                color: 'white'
              }}>
                Cast
              </h3>
              
              {/* Add cast member button (plus icon) */}
              <button
                onClick={handleAddCastMember}
                disabled={showCastSeason[selectedSeason]?.length >= 8}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '1.8rem',
                  height: '1.8rem',
                  backgroundColor: showCastSeason[selectedSeason]?.length >= 8 ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '50%',
                  marginLeft: '0.5rem',
                  cursor: showCastSeason[selectedSeason]?.length >= 8 ? 'not-allowed' : 'pointer'
                }}
              >
                <Plus size={16} color="white" />
              </button>
            </div>
            
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              {/* Season selector */}
              <div style={{ position: 'relative' }}>
                <div 
                  onClick={() => setShowSeasonSelector(!showSeasonSelector)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '0.4rem',
                    cursor: 'pointer',
                    color: 'white'
                  }}
                >
                  Season {selectedSeason}
                  <ChevronDown size={14} />
                </div>
                
                {showSeasonSelector && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '0.25rem',
                    backgroundColor: '#333',
                    borderRadius: '0.4rem',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    zIndex: 20,
                    minWidth: '100%'
                  }}>
                    {[...Array(show.currentSeason || 1)].map((_, i) => (
                      <div 
                        key={i}
                        onClick={() => handleSeasonChange(i + 1)}
                        style={{
                          padding: '0.5rem 1rem',
                          cursor: 'pointer',
                          backgroundColor: selectedSeason === i + 1 ? '#6600CC' : 'transparent',
                          color: 'white',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Season {i + 1}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Create new season button */}
              <button
                onClick={handleCreateNewSeason}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  border: 'none',
                  borderRadius: '0.4rem',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.85rem'
                }}
              >
                <Plus size={14} />
                Create Season {(show.currentSeason || 1) + 1}
              </button>
              
              {/* Delete season button - only show if no episodes in this season and not season 1 */}
              {!hasEpisodesInSeason && selectedSeason > 1 && (
                <button
                  onClick={handleDeleteSeason}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: 'rgba(229, 9, 20, 0.7)',
                    border: 'none',
                    borderRadius: '0.4rem',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '0.85rem'
                  }}
                >
                  <Trash2 size={14} />
                  Delete Season {selectedSeason}
                </button>
              )}
            </div>
          </div>
          
          {/* Cast members grid - 3 per row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '0.75rem'
          }}>
            {showCastSeason[selectedSeason]?.map((castName, index) => (
              <div 
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderRadius: '0.4rem',
                  overflow: 'hidden',
                  position: 'relative'
                }}
              >
                {/* Remove button in top right corner */}
                <div
                  onClick={() => handleRemoveCastMember(index)}
                  style={{
                    position: 'absolute',
                    top: '0',
                    right: '0',
                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                    color: 'white',
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0.25rem',
                    borderRadius: '0 0.4rem 0 0.4rem',
                    zIndex: 5
                  }}
                >
                  <X size={14} />
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.5rem',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  height: '100%'
                }}>
                  <User size={16} color="#ccc" />
                </div>
                
                <input
                  type="text"
                  value={castName}
                  onChange={(e) => handleCastChange(index, e.target.value)}
                  placeholder="Cast Member Name"
                  style={{
                    flex: 1,
                    padding: '0.5rem 0.75rem',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    fontSize: '0.9rem'
                  }}
                />
              </div>
            ))}
          </div>
          
          {/* Cast limit message */}
          {showCastSeason[selectedSeason]?.length >= 8 && (
            <div style={{
              fontSize: '0.8rem',
              color: '#999',
              textAlign: 'center',
              marginTop: '0.5rem'
            }}>
              Maximum of 8 cast members per season
            </div>
          )}
        </div>
        
        {/* Error message */}
        {error && (
          <div style={{
            color: '#E50914',
            fontSize: '0.9rem',
            marginTop: '0.5rem'
          }}>
            {error}
          </div>
        )}
        
        {/* Save button - Full width */}
        <div style={{ marginTop: '1rem', width: '100%' }}>
          <button
            onClick={handleSubmit}
            disabled={saving || !showName.trim() || nameError || descriptionError}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: (!showName.trim() || nameError || descriptionError) 
                ? 'rgba(200, 200, 200, 0.3)' 
                : 'white',
              color: (!showName.trim() || nameError || descriptionError) 
                ? '#999' 
                : '#6600CC',
              border: 'none',
              borderRadius: '0.5rem',
              fontSize: '1rem',
              fontWeight: 'bold',
              cursor: (saving || !showName.trim() || nameError || descriptionError) 
                ? 'not-allowed' 
                : 'pointer',
              opacity: saving ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
      {showCollaboratorsModal && (
        <CollaboratorsModal
          isOpen={showCollaboratorsModal}
          onClose={() => setShowCollaboratorsModal(false)}
          showId={showId}
          showName={showName}
          showDescription={showDescription}
          currentCollaborators={collaborators}
        />
      )}
    </div>
  );
};

export default EditShowModal; 